import { useEffect, useRef, useState } from "react";
import { cn } from "~/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

interface TextOverflowProps extends React.HTMLAttributes<HTMLSpanElement> {
  autoTooltip?: boolean;
  delayDuration?: number;
  /** Number of lines to wrap to before clamping. Defaults to 1 (single-line truncation). */
  lines?: number;
}

const LINE_CLAMP_CLASSES: Record<number, string> = {
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
};

const TextOverflow = ({
  autoTooltip,
  delayDuration,
  lines = 1,
  children,
  className,
  ...props
}: TextOverflowProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () =>
      setOverflowing(
        lines > 1 ? el.scrollHeight > el.clientHeight : el.scrollWidth > el.clientWidth
      );
    check();

    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children, lines]);

  const tooltipEnabled = autoTooltip && overflowing;

  return (
    <Tooltip delayDuration={delayDuration}>
      <TooltipTrigger asChild>
        <span
          ref={ref}
          className={cn(lines > 1 ? LINE_CLAMP_CLASSES[lines] : "truncate", className)}
          {...props}
        >
          {children}
        </span>
      </TooltipTrigger>
      {tooltipEnabled && <TooltipContent className="max-w-[750px]">{children}</TooltipContent>}
    </Tooltip>
  );
};

export { TextOverflow, type TextOverflowProps };
